/**
 * Table-order session for KOT grouping.
 * Lives on the live table cart until checkout clears the table.
 * Ignored by billing/print item loops (no `selected` qty).
 */
export const KOT_SESSION_CART_KEY = '__kotSession';

export interface KotSessionMeta {
  id: string;
  startedAt: number;
}

const createSessionId = (tableNumber: string): string => {
  const tablePart = String(tableNumber || 'table')
    .replace(/[^a-zA-Z0-9-_]/g, '_')
    .slice(0, 24);
  const rand =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().slice(0, 8)
      : Math.random().toString(36).slice(2, 10);
  return `${tablePart}__${Date.now().toString(36)}_${rand}`;
};

export const isKotSessionCartKey = (key: string): boolean =>
  key === KOT_SESSION_CART_KEY || key.startsWith('__');

export function readKotSessionFromCart(
  tableCart: Record<string, unknown> | null | undefined
): KotSessionMeta | null {
  if (!tableCart || typeof tableCart !== 'object') return null;
  const raw = tableCart[KOT_SESSION_CART_KEY] as KotSessionMeta | undefined;
  if (!raw || typeof raw !== 'object' || !raw.id) return null;
  return {
    id: String(raw.id),
    startedAt: Number(raw.startedAt) || Date.now(),
  };
}

/**
 * Returns existing table session or creates one.
 * Persist `sessionMeta` onto the table cart (e.g. via addToCartData) so it
 * survives until checkout / removeToCartData.
 */
export function ensureKotSession(
  tableCart: Record<string, unknown> | null | undefined,
  tableNumber: string
): { sessionId: string; sessionMeta: KotSessionMeta; created: boolean } {
  const existing = readKotSessionFromCart(tableCart);
  if (existing) {
    return { sessionId: existing.id, sessionMeta: existing, created: false };
  }

  const sessionMeta: KotSessionMeta = {
    id: createSessionId(tableNumber),
    startedAt: Date.now(),
  };
  return { sessionId: sessionMeta.id, sessionMeta, created: true };
}

/** Prefer destination table session when merging carts. */
export function pickKotSessionForMerge(
  destCart: Record<string, unknown> | null | undefined,
  sourceCart: Record<string, unknown> | null | undefined,
  destTableNumber: string
): KotSessionMeta {
  return (
    readKotSessionFromCart(destCart) ||
    readKotSessionFromCart(sourceCart) ||
    ensureKotSession(null, destTableNumber).sessionMeta
  );
}
